import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { isAccessory } from '../lib/accessory';
import { CustomPriceDialog } from '../components/CustomPriceDialog';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import {
  ArrowLeft, Plus, Check, Money, CreditCard, Receipt, Trash, Minus, ShoppingCart, ArrowCounterClockwise, Phone, Envelope,
  UsersThree, PencilSimple,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import NumPad from '../components/NumPad';
import { PayConfirmDialog } from '../components/PayConfirmDialog';
import { toast } from 'sonner';
import { useCardPayment } from '../lib/cardTerminal';
import { TipInput, bookTip } from '../components/TipInput';
import { GroupSplitDialog } from '../components/GroupSplitDialog';

const CourtTab = () => {
  const { tabId } = useParams();
  const navigate = useNavigate();
  const { language, API } = useAuth();
  const payByCard = useCardPayment();
  const [tipAmount, setTipAmount] = useState('');
  const [showGroupSplit, setShowGroupSplit] = useState(false);
  const L = (en, deu) => language === 'de' ? deu : en;

  // Return destination — set via ?return_to=/community when the tab was
  // opened from a mixin participant list, so the back-button lands the
  // admin exactly where they came from instead of always /admin/bookings.
  const returnTo = useMemo(() => {
    try {
      const raw = new URLSearchParams(window.location.search).get('return_to') || '';
      // Only accept relative in-app paths — protect against open redirect
      return raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/admin/bookings';
    } catch (_) {
      return '/admin/bookings';
    }
  }, []);

  const [tab, setTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catalogItems, setCatalogItems] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [addCart, setAddCart] = useState({});

  // Checkout selection: { [item.id]: { qty: number, isQuarter: boolean } }
  const [checkout, setCheckout] = useState({});
  const [showPayModal, setShowPayModal] = useState(false);
  const [splitCash, setSplitCash] = useState('');
  const [splitCard, setSplitCard] = useState('');
  const [payLoading, setPayLoading] = useState(false);
  const [customPriceFor, setCustomPriceFor] = useState(null);
  const [numpadFor, setNumpadFor] = useState(null); // {item, current} for Add cart, or {mode:'checkout', item, current, max}
  // Active subscriptions for the tab's user
  const [activeSubs, setActiveSubs] = useState([]);
  // Voucher dialog state: { item, fraction, amount, customer_id, customer_name, vouchers:[], voucher_id }
  const [voucherDialog, setVoucherDialog] = useState(null);
  const [payConfirm, setPayConfirm] = useState(null);

  const loadActiveSubs = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const { data } = await API.get(`/subscriptions?user_id=${uid}&active_only=true`);
      setActiveSubs((Array.isArray(data) ? data : []).filter(s => (s.lessons_remaining || 0) > 0));
    } catch (e) { setActiveSubs([]); }
  }, [API]);

  const loadTab = useCallback(async () => {
    try {
      const { data } = await API.get(`/tabs/${tabId}`);
      setTab(data);
      setCheckout({});
      const uid = data.user_id || data.customer_id;
      if (uid) loadActiveSubs(uid);
    } catch (e) {
      toast.error(L('Tab not found', 'Tab nicht gefunden'));
      navigate(returnTo);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, API]);

  const loadCatalog = useCallback(async () => {
    try {
      const { data } = await API.get('/cash-register/items');
      setCatalogItems(data.filter(i => i.is_active));
    } catch (e) {}
  }, [API]);

  useEffect(() => { loadTab(); loadCatalog(); }, [loadTab, loadCatalog]);

  // ── Batch Add ──
  const cartItemCount = Object.values(addCart).reduce((s, i) => s + i.qty, 0);
  const cartTotal = Object.values(addCart).reduce((s, i) => s + i.price * i.qty, 0);
  const updateCartQty = (item, delta) => {
    if (delta > 0 && isAccessory(item) && !(item.price > 0) && !addCart[item.id]?.custom) { setCustomPriceFor(item); return; }
    setAddCart(prev => {
      const cur = prev[item.id]?.qty || 0;
      const next = cur + delta;
      if (next <= 0) { const { [item.id]: _, ...rest } = prev; return rest; }
      return { ...prev, [item.id]: { ...item, ...(prev[item.id] || {}), qty: next } };
    });
  };
  const applyCustomPrice = (price) => {
    const item = customPriceFor;
    setAddCart(prev => ({ ...prev, [item.id]: { ...item, ...(prev[item.id] || {}), price, custom: true, qty: Math.max(1, prev[item.id]?.qty || 0) } }));
    setCustomPriceFor(null);
  };
  const submitBatchAdd = async () => {
    const items = Object.values(addCart).filter(i => i.qty > 0);
    if (!items.length) return;
    try {
      await API.post(`/tabs/${tabId}/items/batch`, {
        items: items.map(i => ({ name: i.name, price: i.price, quantity: i.qty, category: i.category || 'consumable', item_id: i.id, custom_price: !!i.custom }))
      });
      toast.success(L(`${items.length} item(s) added`, `${items.length} Artikel hinzugefügt`));
      setAddCart({});
      setShowAddItem(false);
      loadTab();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const removeItem = async (itemId) => {
    try {
      await API.delete(`/tabs/${tabId}/items/${itemId}`);
      toast.success(L('Removed', 'Entfernt'));
      loadTab();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const cancelVoucher = async (itemId) => {
    try {
      await API.post(`/tabs/${tabId}/items/${itemId}/cancel-voucher`);
      toast.success(L('Voucher reverted', 'Gutschein rückgängig'));
      loadTab();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const cancelMemberDiscount = async (itemId) => {
    if (!window.confirm(L('Revert the member -20% discount on this item?', 'Member -20% Rabatt auf diesem Item rückgängig machen?'))) return;
    try {
      await API.post(`/tabs/${tabId}/items/${itemId}/cancel-member-discount`);
      toast.success(L('Member discount reverted', 'Member-Rabatt rückgängig'));
      loadTab();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const unpayItem = async (item) => {
    const paidAmt = (item.paid_amount || 0).toFixed(2);
    const confirmMsg = L(
      `Reverse the ${paidAmt} CHF payment on "${item.name}"? A negative entry will be created in Kasse.`,
      `Zahlung von ${paidAmt} CHF auf "${item.name}" rückgängig machen? Ein negativer Eintrag wird in der Kasse erstellt.`,
    );
    if (!window.confirm(confirmMsg)) return;
    setPayLoading(true);
    try {
      await API.put(`/tabs/${tabId}/items/${item.id}/unpay`);
      toast.success(L('Payment reversed — Kasse updated', 'Zahlung rückgängig — Kasse angepasst'));
      loadTab();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Reverse failed', 'Rückgängig fehlgeschlagen'));
    } finally { setPayLoading(false); }
  };

  // ── Checkout selection ──
  const toggleCheckoutItem = (item, delta) => {
    const unpaid = item.quantity - (item.paid_quantity || 0);
    if (unpaid <= 0) return;
    setCheckout(prev => {
      const cur = prev[item.id]?.qty || 0;
      const next = Math.max(0, Math.min(cur + delta, unpaid));
      if (next <= 0) { const { [item.id]: _, ...rest } = prev; return rest; }
      return { ...prev, [item.id]: { qty: next, isQuarter: false, price: item.price } };
    });
  };

  const setCheckoutItemQty = (item, qty) => {
    const unpaid = item.quantity - (item.paid_quantity || 0);
    const next = Math.max(0, Math.min(parseInt(qty, 10) || 0, unpaid));
    setCheckout(prev => {
      if (next <= 0) { const { [item.id]: _, ...rest } = prev; return rest; }
      return { ...prev, [item.id]: { qty: next, isQuarter: false, price: item.price } };
    });
  };

  const toggleQuarter = (item) => {
    setCheckout(prev => {
      if (prev[item.id]?.isQuarter) {
        const { [item.id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [item.id]: { qty: 0, isQuarter: true, price: item.total / 4 } };
    });
  };

  const checkoutTotal = Object.entries(checkout).reduce((sum, [id, sel]) => {
    const item = tab?.items.find(i => i.id === id);
    if (!item) return sum;
    if (sel.isVoucher) return sum; // voucher contributes 0
    if (sel.isQuarter) {
      // 1/4 always based on the ORIGINAL price (vouchers don't shrink the quarter share)
      const base = item.original_total || item.total;
      const remaining = item.total - (item.paid_amount || 0);
      return sum + Math.min(base / 4, remaining);
    }
    if (sel.remainingAmount !== undefined) {
      // "Rest" for court fee — pay whatever is remaining
      return sum + sel.remainingAmount;
    }
    return sum + sel.price * sel.qty;
  }, 0);

  const checkoutCount = Object.values(checkout).reduce((s, sel) => s + (sel.isQuarter ? 1 : (sel.isVoucher ? 1 : sel.qty)), 0);
  const hasVoucherSelection = Object.values(checkout).some(sel => sel.isVoucher);

  // Apply voucher immediately on click — no separate "Bezahlen" step needed
  const applyVoucherImmediate = async (item, fraction) => {
    try {
      setPayLoading(true);
      await API.put(`/tabs/${tabId}/checkout`, {
        items: [{ item_id: item.id, is_voucher: true, voucher_fraction: fraction, quantity: item.quantity - (item.paid_quantity || 0) }],
        cash_amount: 0, card_amount: 0,
      });
      const fLabel = Math.abs(fraction - 0.25) < 0.01 ? '1/4' : Math.abs(fraction - 0.5) < 0.01 ? '1/2' : L('full', 'ganz');
      toast.success(L(`Voucher ${fLabel} applied`, `Gutschein ${fLabel} angewendet`));
      setCheckout(prev => { const { [item.id]: _, ...rest } = prev; return rest; });
      loadTab();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Failed', 'Fehlgeschlagen'));
    } finally { setPayLoading(false); }
  };

  // Apply Senior-Abo immediately — marks the chosen share (1/4, 1/2 or full)
  // as PAID but creates NO cash_register entry at all. Senior Abos are
  // pre-paid annually outside the Kasse flow, so each use must not show
  // up on the daily Tagesabrechnung.
  const applySeniorAboImmediate = async (item, fraction, aboLabel = 'Senior-Abo') => {
    try {
      setPayLoading(true);
      await API.put(`/tabs/${tabId}/checkout`, {
        items: [{ item_id: item.id, is_senior_abo: true, senior_abo_fraction: fraction, abo_label: aboLabel, quantity: item.quantity - (item.paid_quantity || 0) }],
        cash_amount: 0, card_amount: 0,
      });
      const fLabel = Math.abs(fraction - 0.25) < 0.01 ? '1/4' : Math.abs(fraction - 0.5) < 0.01 ? '1/2' : L('full', 'ganz');
      toast.success(`${aboLabel} ${fLabel} ${L('applied', 'angewendet')}`);
      setCheckout(prev => { const { [item.id]: _, ...rest } = prev; return rest; });
      loadTab();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Failed', 'Fehlgeschlagen'));
    } finally { setPayLoading(false); }
  };

  // Apply CSS Coins immediately — marks the chosen share (1/4, 1/2 or full)
  // as PAID but books it under method="css_coins" so it does NOT land in
  // the daily cash/card Tagesabrechnung. CSS Coins payout to the club
  // happens separately (~2 weeks after the transaction).
  const applyCssCoinsImmediate = async (item, fraction) => {
    try {
      setPayLoading(true);
      await API.put(`/tabs/${tabId}/checkout`, {
        items: [{ item_id: item.id, is_css_coins: true, css_coins_fraction: fraction, quantity: item.quantity - (item.paid_quantity || 0) }],
        cash_amount: 0, card_amount: 0,
      });
      const fLabel = Math.abs(fraction - 0.25) < 0.01 ? '1/4' : Math.abs(fraction - 0.5) < 0.01 ? '1/2' : L('full', 'ganz');
      toast.success(L(`CSS Coins ${fLabel} applied`, `CSS Coins ${fLabel} angewendet`));
      setCheckout(prev => { const { [item.id]: _, ...rest } = prev; return rest; });
      loadTab();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Failed', 'Fehlgeschlagen'));
    } finally { setPayLoading(false); }
  };

  // Open the "which voucher & how much" dialog — replaces the old
  // one-click apply. The dialog forces the admin to pick a specific
  // customer voucher and an exact amount, so the deduction is auditable.
  const openVoucherDialog = async (item, defaultFraction = 1.0) => {
    const openAmt = (item.total || 0) - (item.paid_amount || 0);
    const suggested = item.allows_quarter
      ? Number(((item.original_total || item.total || 0) * defaultFraction).toFixed(2))
      : Number(openAmt.toFixed(2));
    const defaultCustomerId = tab?.user_id || tab?.customer_id || '';
    const defaultCustomerName = tab?.customer_name || '';
    let vouchers = [];
    if (defaultCustomerId) {
      try {
        const { data } = await API.get(`/vouchers/user/${defaultCustomerId}`);
        vouchers = Array.isArray(data) ? data : [];
      } catch (_e) { /* no vouchers → dialog still opens with search box */ }
    }
    setVoucherDialog({
      item, fraction: defaultFraction,
      amount: String(Math.min(suggested, openAmt).toFixed(2)),
      customer_id: defaultCustomerId,
      customer_name: defaultCustomerName,
      vouchers,
      voucher_id: vouchers[0]?.id || '',
      loading: false,
    });
  };

  const changeVoucherCustomer = async (uid, uname) => {
    setVoucherDialog(prev => prev ? { ...prev, customer_id: uid, customer_name: uname, vouchers: [], voucher_id: '', loading: true } : prev);
    try {
      const { data } = await API.get(`/vouchers/user/${uid}`);
      const list = Array.isArray(data) ? data : [];
      setVoucherDialog(prev => prev ? { ...prev, vouchers: list, voucher_id: list[0]?.id || '', loading: false } : prev);
    } catch (_e) {
      setVoucherDialog(prev => prev ? { ...prev, vouchers: [], voucher_id: '', loading: false } : prev);
    }
  };

  const submitVoucherDialog = async () => {
    if (!voucherDialog) return;
    const amt = parseFloat(voucherDialog.amount);
    if (!voucherDialog.voucher_id) {
      toast.error(L('Please pick a voucher', 'Bitte einen Gutschein auswählen'));
      return;
    }
    if (!(amt > 0)) {
      toast.error(L('Amount must be positive', 'Betrag muss positiv sein'));
      return;
    }
    try {
      setPayLoading(true);
      await API.post(`/tabs/${tabId}/items/${voucherDialog.item.id}/apply-voucher-specific`, {
        voucher_id: voucherDialog.voucher_id,
        amount: amt,
      });
      toast.success(L(`Voucher ${amt.toFixed(2)} CHF applied`, `Gutschein ${amt.toFixed(2)} CHF angewendet`));
      setVoucherDialog(null);
      loadTab();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Voucher apply failed', 'Gutschein-Anwendung fehlgeschlagen'));
    } finally { setPayLoading(false); }
  };

  // Apply member -20% discount immediately — 20% off the given share of the
  // ORIGINAL court fee (mirrors the voucher UX). The customer then pays the
  // remaining 80% via cash / card / voucher in a normal checkout.
  const applyMemberImmediate = async (item, fraction) => {
    try {
      setPayLoading(true);
      await API.put(`/tabs/${tabId}/checkout`, {
        items: [{ item_id: item.id, is_member_share: true, member_share_fraction: fraction }],
        cash_amount: 0, card_amount: 0,
      });
      const fLabel = Math.abs(fraction - 0.25) < 0.01 ? '1/4' : Math.abs(fraction - 0.5) < 0.01 ? '1/2' : L('full', 'ganz');
      toast.success(L(`Member -20% (${fLabel}) applied`, `Member -20% (${fLabel}) angewendet`));
      setCheckout(prev => { const { [item.id]: _, ...rest } = prev; return rest; });
      loadTab();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Failed', 'Fehlgeschlagen'));
    } finally { setPayLoading(false); }
  };

  // Pay one court-fee item using a subscription (Abo) — no cash / no Tagesbericht
  const payViaSubscription = async (sub) => {
    if (!tab) return;
    try {
      setPayLoading(true);
      await API.post(`/subscriptions/${sub.id}/use`, { booking_id: tab.booking_id });
      toast.success(L('Paid via subscription', 'Über Abo bezahlt'));
      loadTab();
      loadActiveSubs(tab.user_id || tab.customer_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Failed', 'Fehlgeschlagen'));
    } finally { setPayLoading(false); }
  };

  const runPayConfirm = async () => {
    const { kind, item, fraction, sub } = payConfirm;
    if (kind === 'senior') await applySeniorAboImmediate(item, fraction);
    else if (kind === 'jahres') await applySeniorAboImmediate(item, fraction, 'Jahres-Abo');
    else if (kind === 'css') await applyCssCoinsImmediate(item, fraction);
    else if (kind === 'member') await applyMemberImmediate(item, fraction);
    else await payViaSubscription(sub);
    setPayConfirm(null);
  };

  const openCheckoutPay = () => {
    if (checkoutTotal <= 0) return;
    setSplitCash(checkoutTotal.toFixed(2));
    setSplitCard('0.00');
    setShowPayModal(true);
  };

  const handleSplitCashChange = (val) => {
    setSplitCash(val);
    setSplitCard(Math.max(0, checkoutTotal - (parseFloat(val) || 0)).toFixed(2));
  };
  const handleSplitCardChange = (val) => {
    setSplitCard(val);
    setSplitCash(Math.max(0, checkoutTotal - (parseFloat(val) || 0)).toFixed(2));
  };

  const confirmCheckout = async () => {
    setPayLoading(true);
    try {
      const cash = parseFloat(splitCash) || 0;
      const card = parseFloat(splitCard) || 0;
      const tip = Math.max(0, parseFloat(tipAmount) || 0);
      const term = await payByCard(card + tip, `Tab ${tab?.customer_name || tabId}`);
      if (!term.ok) {
        toast.error(`${L('Card payment not completed', 'Kartenzahlung nicht abgeschlossen')}: ${term.reason}${term.uncertain ? '' : ` — ${L('nothing booked', 'nichts gebucht')}`}`, { duration: 10000 });
        return;
      }
      const itemsPayload = Object.entries(checkout).map(([id, sel]) => ({
        item_id: id,
        quantity: sel.qty,
        is_quarter: sel.isQuarter || false,
        is_voucher: sel.isVoucher || false,
        voucher_fraction: sel.voucherFraction || 1.0,
      }));
      await API.put(`/tabs/${tabId}/checkout`, {
        items: itemsPayload,
        cash_amount: cash,
        card_amount: card
      });
      await bookTip(API, tip, `Tab ${tab?.customer_name || ''}`.trim());
      setTipAmount('');
      toast.success(tip > 0 ? `${L('Payment recorded', 'Zahlung erfasst')} + ${tip.toFixed(2)} CHF ${L('tip', 'Trinkgeld')}` : L('Payment recorded!', 'Zahlung erfasst!'));
      setShowPayModal(false);
      setCheckout({});
      loadTab();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Payment failed', 'Zahlung fehlgeschlagen'));
    } finally { setPayLoading(false); }
  };

  // Pay the whole selection with CSS Coins (booked as css_coins, not in the daily till).
  const confirmCssCheckout = async () => {
    setPayLoading(true);
    try {
      const itemsPayload = Object.entries(checkout).map(([id, sel]) => (sel.isVoucher
        ? { item_id: id, quantity: sel.qty, is_voucher: true, voucher_fraction: sel.voucherFraction || 1.0 }
        : { item_id: id, quantity: sel.qty, is_css_coins: true, css_coins_fraction: sel.isQuarter ? 0.25 : 1.0 }));
      await API.put(`/tabs/${tabId}/checkout`, { items: itemsPayload, cash_amount: 0, card_amount: 0 });
      toast.success(L('Paid with CSS Coins', 'Mit CSS Coins bezahlt'));
      setShowPayModal(false);
      setTipAmount('');
      setCheckout({});
      loadTab();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Payment failed', 'Zahlung fehlgeschlagen'));
    } finally { setPayLoading(false); }
  };

  // Pay all remaining (cash/card) — vouchers already reduced the totals
  const payAllRemaining = () => {
    if (!tab) return;
    const sel = {};
    let total = 0;
    for (const item of tab.items) {
      if (item.paid) continue;
      const remaining = (item.total || 0) - (item.paid_amount || 0);
      if (remaining <= 0) continue;
      if (item.allows_quarter) {
        // Court fee: pay the exact remaining (after voucher discount)
        sel[item.id] = { qty: item.quantity - (item.paid_quantity || 0), isQuarter: false, price: item.price, remainingAmount: remaining };
      } else {
        const unpaidQty = item.quantity - (item.paid_quantity || 0);
        sel[item.id] = { qty: unpaidQty, isQuarter: false, price: item.price };
      }
      total += remaining;
    }
    setCheckout(sel);
    setSplitCash(total.toFixed(2));
    setSplitCard('0.00');
    setShowPayModal(true);
  };

  if (loading) return <div className="p-8 text-center text-slate-400">{L('Loading...', 'Laden...')}</div>;
  if (!tab) return null;

  const remaining = Math.max(0, Math.round((tab.total_amount - tab.paid_amount) * 100) / 100);
  const isClosed = tab.status === 'closed';

  // Grouped catalog for the "Add to Tab" dialog. Display order is opinionated
  // (staff request, Feb 2026): rental rackets / balls first (fastest sellers
  // at the counter), then alcoholic drinks, then non-alcoholic drinks, then
  // everything else (snacks, court fees, etc.). Unknown categories fall to
  // the bottom in alphabetical order so new categories don't disappear.
  const groupedCatalog = catalogItems.reduce((acc, item) => {
    const cat = item.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});
  const categoryRank = (cat) => {
    const c = (cat || '').toLowerCase();
    if (c.includes('schläger') || c.includes('schlager') || c.includes('racket')) return 1;
    if (c.includes('equipment') || c.includes('ball')) return 2;
    if (c.includes('alkoholisch') && !c.includes('alkoholfrei')) return 3;
    if (c.includes('alcohol') && !c.includes('non')) return 3;
    if (c.includes('alkoholfrei') || c.includes('non-alcoholic') || c.includes('softdrink')) return 4;
    if (c.includes('snack') || c.includes('food')) return 5;
    return 99;
  };
  const orderedCatalogEntries = Object.entries(groupedCatalog).sort(([a], [b]) => {
    const ra = categoryRank(a);
    const rb = categoryRank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b, 'de');
  });

  return (
    <div className="space-y-5" data-testid="court-tab-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(returnTo)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="tab-back-btn">
          <ArrowLeft size={20} className="text-slate-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">{tab.court_name}</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${isClosed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="tab-status">
              {isClosed ? L('Closed', 'Geschlossen') : L('Open', 'Offen')}
            </span>
          </div>
          <p className="text-slate-500 text-sm mt-0.5">{tab.customer_name} — {tab.date} {tab.time}</p>
          {(tab.customer_email || tab.customer_phone) && (
            <div className="flex items-center gap-3 mt-1.5 flex-wrap" data-testid="tab-contact-info">
              {tab.customer_phone && (
                <a
                  href={`tel:${tab.customer_phone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 rounded-md transition"
                  data-testid="tab-contact-phone"
                >
                  <Phone size={12} weight="fill" />
                  {tab.customer_phone}
                </a>
              )}
              {tab.customer_email && (
                <a
                  href={`mailto:${tab.customer_email}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded-md transition"
                  data-testid="tab-contact-email"
                >
                  <Envelope size={12} weight="fill" />
                  {tab.customer_email}
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 p-4 rounded-2xl">
          <p className="text-xs text-slate-400 uppercase font-semibold">Total</p>
          <p className="text-2xl font-extrabold text-slate-900" data-testid="tab-total">{tab.total_amount.toFixed(2)} CHF</p>
        </div>
        <div className="bg-white border border-slate-200 p-4 rounded-2xl">
          <p className="text-xs text-slate-400 uppercase font-semibold">{L('Paid', 'Bezahlt')}</p>
          <p className="text-2xl font-extrabold text-emerald-600" data-testid="tab-paid">{tab.paid_amount.toFixed(2)} CHF</p>
        </div>
        <div className="bg-white border border-slate-200 p-4 rounded-2xl">
          <p className="text-xs text-slate-400 uppercase font-semibold">{L('Remaining', 'Offen')}</p>
          <p className={`text-2xl font-extrabold ${remaining > 0 ? 'text-red-500' : 'text-emerald-600'}`} data-testid="tab-remaining">{remaining.toFixed(2)} CHF</p>
        </div>
      </div>

      {/* Subscription banner — show available abos for tab user */}
      {!isClosed && (tab.user_id || tab.customer_id) && activeSubs.length > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-2xl p-4" data-testid="abo-banner">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-bold text-purple-900">{L('Active Subscriptions', 'Aktive Abos')} — {tab.customer_name}</p>
              <p className="text-xs text-purple-700">{L('Click "Pay via Abo" on the court item below', 'Klick "Vom Abo bezahlen" beim Court-Eintrag unten')}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {activeSubs.map(s => (
                <div key={s.id} className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 ${s.type === 'training' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`} data-testid={`abo-tag-${s.id}`}>
                  <span>{s.type === 'training' ? L('Training', 'Training') : L('Court', 'Court')}</span>
                  <span className="font-extrabold">{s.lessons_remaining}/{s.total_lessons}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab Items with checkout selection */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-bold text-slate-900">{L('Items', 'Positionen')}</h2>
          <div className="flex items-center gap-2">
            {!isClosed && remaining > 0 && (
              <Button onClick={payAllRemaining} size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold text-xs" data-testid="select-all-btn">
                {L('Select All', 'Alle auswählen')}
              </Button>
            )}
            {!isClosed && (
              <Button onClick={() => { setAddCart({}); setShowAddItem(true); }} size="sm" className="bg-slate-800 text-white hover:bg-slate-700 font-semibold" data-testid="add-item-btn">
                <Plus size={16} className="mr-1" /> {L('Add Items', 'Hinzufügen')}
              </Button>
            )}
          </div>
        </div>
        <div className="divide-y divide-slate-50">
          {tab.items.map((item) => {
            const paidQty = item.paid_quantity || 0;
            const unpaidQty = item.quantity - paidQty;
            const sel = checkout[item.id];
            const selQty = sel?.qty || 0;
            const selQuarter = sel?.isQuarter || false;

            return (
              <div key={item.id} className={`px-5 py-3 flex items-center gap-3 transition-all ${item.paid ? 'bg-emerald-50/50' : sel ? 'bg-blue-50/50' : ''}`} data-testid={`tab-item-${item.id}`}>
                {/* Selection controls */}
                {!item.paid && !isClosed && (
                  <div className="flex flex-col items-center gap-1 min-w-[70px]">
                    {item.allows_quarter ? (
                      /* Court fee: Abo + 1/4 + Rest + 3 Gutschein-Optionen */
                      <div className="flex flex-col gap-1">
                        {/* Subscription buttons */}
                        {!item.paid && activeSubs.length > 0 && (
                          <div className="flex gap-1 flex-wrap">
                            {activeSubs.map(s => (
                              <button key={s.id} onClick={() => setPayConfirm({ kind: 'abo', item, sub: s })}
                                disabled={payLoading}
                                className={`px-2 py-1 text-[11px] font-bold rounded-lg border transition-all disabled:opacity-50 ${s.type === 'training' ? 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-500 hover:text-white hover:border-blue-500' : 'border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-500 hover:text-white hover:border-emerald-500'}`}
                                data-testid={`pay-abo-${s.id}`}>
                                {s.type === 'training' ? L('Training Abo', 'Training-Abo') : L('Court Abo', 'Court-Abo')} ({s.lessons_remaining}/{s.total_lessons})
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-1">
                          <button onClick={() => toggleQuarter(item)}
                            className={`px-2 py-1 text-xs font-bold rounded-lg border transition-all ${selQuarter ? 'bg-blue-500 text-white border-blue-500' : 'border-slate-200 text-slate-500 hover:border-blue-300'}`}
                            data-testid={`quarter-${item.id}`}>
                            1/4
                          </button>
                          <button onClick={() => {
                            if (selQty > 0) { setCheckout(prev => { const { [item.id]: _, ...rest } = prev; return rest; }); }
                            else {
                              const remainingAmt = item.total - (item.paid_amount || 0);
                              setCheckout(prev => ({ ...prev, [item.id]: { qty: unpaidQty, isQuarter: false, price: item.price, remainingAmount: remainingAmt } }));
                            }
                          }}
                            className={`px-2 py-1 text-xs font-bold rounded-lg border transition-all ${selQty > 0 && !selQuarter && !sel?.isVoucher ? 'bg-blue-500 text-white border-blue-500' : 'border-slate-200 text-slate-500 hover:border-blue-300'}`}
                            data-testid={`full-court-${item.id}`}>
                            {L('Rest', 'Rest')}
                          </button>
                        </div>
                        {/* Gutschein 1/4, 1/2, ganz — ONE-CLICK: applies immediately */}
                        <div className="flex gap-1">
                          {[
                            { f: 0.25, label: L('G 1/4', 'G 1/4') },
                            { f: 0.5,  label: L('G 1/2', 'G 1/2') },
                            { f: 1.0,  label: L('G full', 'G ganz') },
                          ].map(({ f, label }) => (
                            <button key={f}
                              onClick={() => openVoucherDialog(item, f)}
                              disabled={payLoading}
                              className="px-2 py-1 text-[11px] font-bold rounded-lg border transition-all border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-500 hover:text-white hover:border-purple-500 disabled:opacity-50"
                              data-testid={`voucher-${f}-${item.id}`}>
                              {label}
                            </button>
                          ))}
                        </div>
                        {/* CSS Coins 1/4, 1/2, ganz — one-click. Marks the
                            share as paid but books it under css_coins so it
                            does NOT contaminate the daily Tagesabrechnung
                            (CSS payout arrives ~2 weeks later). */}
                        <div className="flex gap-1">
                          {[
                            { f: 0.25, label: L('CSS 1/4', 'CSS 1/4') },
                            { f: 0.5,  label: L('CSS 1/2', 'CSS 1/2') },
                            { f: 1.0,  label: L('CSS full', 'CSS ganz') },
                          ].map(({ f, label }) => (
                            <button key={f}
                              onClick={() => setPayConfirm({ kind: 'css', item, fraction: f })}
                              disabled={payLoading}
                              className="px-2 py-1 text-[11px] font-bold rounded-lg border transition-all border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50 hover:bg-fuchsia-600 hover:text-white hover:border-fuchsia-600 disabled:opacity-50"
                              title={L('CSS Coins — not counted in daily till (~2 weeks payout)', 'CSS Coins — nicht im Tages-Total (Auszahlung ~2 Wochen)')}
                              data-testid={`css-${f}-${item.id}`}>
                              {label}
                            </button>
                          ))}
                        </div>
                        {/* Senior-Abo 1/4, 1/2, ganz — one-click. Marks the
                            chosen share as PAID with ZERO Kasse entry at all
                            (pre-paid outside the Kasse flow). */}
                        {item.category === 'court_fee' && (
                          <div className="flex gap-1">
                            {[
                              { f: 0.25, label: L('Senior 1/4', 'Senior 1/4') },
                              { f: 0.5,  label: L('Senior 1/2', 'Senior 1/2') },
                              { f: 1.0,  label: L('Senior full', 'Senior ganz') },
                            ].map(({ f, label }) => (
                              <button key={f}
                                onClick={() => setPayConfirm({ kind: 'senior', item, fraction: f })}
                                disabled={payLoading}
                                className="px-2 py-1 text-[11px] font-bold rounded-lg border transition-all border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-500 hover:text-white hover:border-amber-500 disabled:opacity-50"
                                title={L('Senior Abo — NO Kasse entry at all', 'Senior-Abo — KEIN Kassen-Eintrag')}
                                data-testid={`senior-${f}-${item.id}`}>
                                {label}
                              </button>
                            ))}
                          </div>
                        )}
                        {item.category === 'court_fee' && (
                          <div className="flex gap-1">
                            {[
                              { f: 1.0, label: L('Yearly Abo (full court)', 'Jahres-Abo (ganzer Platz)') },
                            ].map(({ f, label }) => (
                              <button key={f}
                                onClick={() => setPayConfirm({ kind: 'jahres', item, fraction: f })}
                                disabled={payLoading}
                                className="px-2 py-1 text-[11px] font-bold rounded-lg border transition-all border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 disabled:opacity-50"
                                title={L('Yearly Abo — NO Kasse entry at all', 'Jahres-Abo — KEIN Kassen-Eintrag')}
                                data-testid={`jahres-${f}-${item.id}`}>
                                {label}
                              </button>
                            ))}
                          </div>
                        )}
                        {/* Member -20% on 1/4 share — 20% off the player's
                            court quarter. Customer then pays the remaining
                            80% via cash/card/voucher. Only the 1/4 flavour
                            is exposed: members practically never pay 1/2 or
                            the whole court solo, so we keep the UI tight. */}
                        <div className="flex gap-1">
                          <button
                            onClick={() => setPayConfirm({ kind: 'member', item, fraction: 0.25 })}
                            disabled={payLoading}
                            className="px-2 py-1 text-[11px] font-bold rounded-lg border transition-all border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-500 hover:text-white hover:border-amber-500 disabled:opacity-50"
                            title={L('Apply 20% member discount on 1/4 share', '20% Member-Rabatt auf 1/4-Anteil anwenden')}
                            data-testid={`member-0.25-${item.id}`}
                          >
                            {L('M 1/4', 'M 1/4')}
                          </button>
                        </div>
                      </div>
                    ) : unpaidQty > 0 ? (
                      /* Regular items: qty selector + voucher button */
                      <div className="flex flex-col items-stretch gap-1">
                        <div className="flex items-center border border-slate-200 rounded-lg bg-white">
                          <button onClick={() => toggleCheckoutItem(item, -1)} className="px-2 py-1 text-slate-500 hover:bg-slate-50" data-testid={`checkout-dec-${item.id}`}>
                            <Minus size={12} weight="bold" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setNumpadFor({ mode: 'checkout', item, current: selQty, max: unpaidQty })}
                            className={`px-2 py-1 min-w-[28px] text-center text-sm font-bold border-x border-slate-200 transition-colors hover:bg-blue-50 active:scale-95 ${selQty > 0 && !sel?.isVoucher ? 'text-blue-700' : 'text-slate-300'}`}
                            data-testid={`checkout-qty-${item.id}`}
                            title={L('Tap to type quantity', 'Antippen zum Eingeben')}>
                            {selQty}
                          </button>
                          <button onClick={() => toggleCheckoutItem(item, 1)} className="px-2 py-1 text-slate-500 hover:bg-slate-50" data-testid={`checkout-inc-${item.id}`}>
                            <Plus size={12} weight="bold" />
                          </button>
                        </div>
                        <button onClick={() => openVoucherDialog(item, 1.0)}
                          disabled={payLoading}
                          className="px-2 py-0.5 text-[10px] font-bold rounded-lg border transition-all border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-500 hover:text-white hover:border-purple-500 disabled:opacity-50"
                          data-testid={`voucher-${item.id}`}>
                          {L('Voucher', 'Gutschein')}
                        </button>
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Item info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`font-medium text-sm ${item.paid ? 'text-emerald-700 line-through' : 'text-slate-900'}`}>{item.name}</p>
                    {item.quantity > 1 && (
                      <span className="text-xs text-slate-500">
                        x{item.quantity} à {Number(item.price).toFixed(2)}
                      </span>
                    )}
                    {item.custom_price && (
                      <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold" data-testid={`tab-item-custom-${item.id}`}>{L('custom price', 'ind. Preis')}</span>
                    )}
                    {item.quantity > 1 && paidQty > 0 && !item.paid && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-800 border border-amber-300" data-testid={`open-qty-${item.id}`}>
                        {unpaidQty} {L('open', 'offen')}
                      </span>
                    )}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${item.category === 'court_fee' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                      {item.category === 'court_fee' ? L('Padelcourt', 'Padelcourt') : item.category}
                    </span>
                    {item.paid_via_abo_label && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`abo-label-${item.id}`}>
                        {item.paid_via_abo_label}
                      </span>
                    )}
                    {item.pre_paid && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
                        {L('Already paid', 'Bereits bezahlt')}
                      </span>
                    )}
                  </div>
                  {(item.online_paid_amount || 0) > 0 && (
                    <p className="text-xs text-sky-700 font-semibold mt-0.5" data-testid={`online-paid-note-${item.id}`}>
                      {L('Paid online', 'Online bezahlt')}: {item.online_paid_amount.toFixed(2)} CHF · {(item.online_paid_names || []).join(', ')}
                    </p>
                  )}
                  {paidQty > 0 && !item.paid && (
                    <p className="text-xs text-amber-600 mt-0.5">{paidQty}× {L('paid', 'bezahlt')} — {unpaidQty}× {L('open', 'offen')}</p>
                  )}
                  {item.allows_quarter && (item.paid_amount || 0) > 0 && !item.paid && (
                    <p className="text-xs text-amber-600 mt-0.5">{item.paid_amount.toFixed(2)} / {item.total.toFixed(2)} CHF {L('paid', 'bezahlt')}</p>
                  )}
                  {/* Show selected amount */}
                  {sel && !sel.isVoucher && (
                    <p className="text-xs font-semibold mt-0.5 text-blue-600">
                      {selQuarter ? (() => {
                          const base = item.original_total || item.total;
                          const q = Math.min(base / 4, item.total - (item.paid_amount || 0));
                          return `1/4 = ${q.toFixed(2)} CHF`;
                        })()
                        : sel.remainingAmount !== undefined ? `${L('Remaining', 'Rest')} = ${sel.remainingAmount.toFixed(2)} CHF`
                        : `${selQty}× ${item.price.toFixed(2)} = ${(selQty * item.price).toFixed(2)} CHF`}
                    </p>
                  )}
                </div>

                {/* Price */}
                <div className="text-right whitespace-nowrap">
                  {((item.voucher_amount > 0) || (item.member_discount_amount > 0)) && item.original_total && item.original_total > item.total && (
                    <p className="text-[10px] text-slate-400 line-through leading-none">{item.original_total.toFixed(2)} CHF</p>
                  )}
                  <p className={`font-bold text-sm leading-tight ${item.paid ? 'text-emerald-600' : 'text-slate-900'}`}>
                    {item.total.toFixed(2)} CHF
                  </p>
                  {item.voucher_amount > 0 && (
                    <p className="text-[10px] text-purple-600 font-semibold leading-none">−{item.voucher_amount.toFixed(2)} {L('Voucher', 'Gutschein')}</p>
                  )}
                  {item.member_discount_amount > 0 && (
                    <p className="text-[10px] text-amber-700 font-semibold leading-none">−{item.member_discount_amount.toFixed(2)} {L('Member -20%', 'Member -20%')}</p>
                  )}
                </div>

                {/* Status */}
                {item.paid ? (
                  <div className="flex items-center gap-1">
                    <span className="text-emerald-600"><Check size={20} weight="bold" /></span>
                    {!isClosed && item.voucher_amount > 0 && (
                      <button
                        onClick={() => cancelVoucher(item.id)}
                        title={L('Undo voucher', 'Gutschein rückgängig')}
                        className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                        data-testid={`cancel-voucher-${item.id}`}
                      >
                        <ArrowCounterClockwise size={14} />
                      </button>
                    )}
                    {!isClosed && item.member_discount_amount > 0 && (
                      <button
                        onClick={() => cancelMemberDiscount(item.id)}
                        title={L('Undo member -20%', 'Member -20% rückgängig')}
                        className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg"
                        data-testid={`cancel-member-${item.id}`}
                      >
                        <ArrowCounterClockwise size={14} />
                      </button>
                    )}
                    {!isClosed && !item.pre_paid && (
                      <button
                        onClick={() => unpayItem(item)}
                        disabled={payLoading}
                        title={L('Reverse payment (creates a negative Kasse entry)', 'Zahlung rückgängig (schreibt negativen Kasse-Eintrag)')}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                        data-testid={`unpay-item-${item.id}`}
                      >
                        <ArrowCounterClockwise size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    {!isClosed && item.voucher_amount > 0 && (
                      <button
                        onClick={() => cancelVoucher(item.id)}
                        title={L('Undo voucher', 'Gutschein rückgängig')}
                        className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                        data-testid={`cancel-voucher-${item.id}`}
                      >
                        <ArrowCounterClockwise size={14} />
                      </button>
                    )}
                    {!isClosed && item.member_discount_amount > 0 && (
                      <button
                        onClick={() => cancelMemberDiscount(item.id)}
                        title={L('Undo member -20%', 'Member -20% rückgängig')}
                        className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg"
                        data-testid={`cancel-member-unpaid-${item.id}`}
                      >
                        <ArrowCounterClockwise size={14} />
                      </button>
                    )}
                    {!isClosed && item.category !== 'court_fee' && unpaidQty > 0 && (
                      <button onClick={() => removeItem(item.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg" data-testid={`remove-item-${item.id}`}><Trash size={14} /></button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Checkout Bar — fixed at bottom */}
      {checkoutCount > 0 && !isClosed && (
        <div className="sticky bottom-0 bg-white border border-slate-200 rounded-2xl p-4 shadow-lg" data-testid="checkout-bar">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <ShoppingCart size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="font-bold text-slate-900">{checkoutCount} {L('item(s) selected', 'Position(en) ausgewählt')}</p>
                <p className="text-xs text-slate-500">{L('Ready to pay', 'Bereit zum Bezahlen')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setCheckout({})} className="text-sm text-slate-400 hover:text-slate-600" data-testid="clear-checkout">
                {L('Clear', 'Leeren')}
              </button>
              <Button onClick={openCheckoutPay} className="bg-emerald-500 text-white hover:bg-emerald-600 font-bold text-base px-6 py-3" data-testid="checkout-pay-btn">
                <Receipt size={18} className="mr-2" /> {checkoutTotal.toFixed(2)} CHF {L('Pay', 'Bezahlen')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {tab?.booking_id && !isClosed && (
        <Button variant="outline" onClick={() => setShowGroupSplit(true)} className="w-full border-emerald-300 text-emerald-800 hover:bg-emerald-50 font-bold" data-testid="group-split-btn">
          <UsersThree size={18} className="mr-2" /> {L('Split court fee across players', 'Platzmiete auf Spieler aufteilen')}
        </Button>
      )}
      <GroupSplitDialog open={showGroupSplit} onClose={() => setShowGroupSplit(false)} tabId={tabId} API={API} L={L} onPaid={loadTab} />

      {/* Pay All button when nothing selected */}
      {checkoutCount === 0 && remaining > 0 && !isClosed && (
        <Button onClick={payAllRemaining} className="w-full bg-emerald-500 text-white hover:bg-emerald-600 font-bold text-base py-6 rounded-xl" data-testid="pay-all-btn">
          <Receipt size={20} className="mr-2" /> {L(`Pay All — ${remaining.toFixed(2)} CHF`, `Alles bezahlen — ${remaining.toFixed(2)} CHF`)}
        </Button>
      )}

      <CustomPriceDialog item={customPriceFor} onConfirm={applyCustomPrice} onClose={() => setCustomPriceFor(null)} L={L} />
      {/* ===== ADD ITEMS MODAL ===== */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg rounded-2xl max-h-[85vh] flex flex-col" data-testid="add-item-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{L('Add to Tab', 'Zum Tab hinzufügen')}</DialogTitle>
            <DialogDescription className="text-slate-500">{L('Select items and quantities', 'Artikel und Mengen auswählen')}</DialogDescription>
          </DialogHeader>
          {cartItemCount > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center justify-between" data-testid="add-cart-summary">
              <span className="text-sm font-medium text-emerald-800">{cartItemCount} {L('item(s)', 'Artikel')} — {cartTotal.toFixed(2)} CHF</span>
              <Button size="sm" onClick={submitBatchAdd} className="bg-emerald-500 text-white hover:bg-emerald-600 font-bold px-4" data-testid="submit-batch-add">
                <Plus size={14} className="mr-1" /> {L('Add All', 'Alle hinzufügen')}
              </Button>
            </div>
          )}
          <div className="overflow-y-auto flex-1 space-y-4 mt-2">
            {orderedCatalogEntries.map(([cat, catItems]) => (
              <div key={cat}>
                <p className="text-xs text-slate-400 uppercase font-semibold mb-2">{cat}</p>
                <div className="space-y-1.5">
                  {catItems.map(item => {
                    const qty = addCart[item.id]?.qty || 0;
                    const customPrice = addCart[item.id]?.custom ? addCart[item.id].price : null;
                    return (
                      <div key={item.id} className={`flex items-center gap-3 p-2.5 border rounded-xl transition-all ${qty > 0 ? 'border-emerald-400 bg-emerald-50/50' : 'border-slate-200'}`} data-testid={`catalog-item-${item.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900">{item.name}</p>
                          <p className="text-emerald-600 font-bold text-xs">
                            {customPrice != null ? (<><span className="line-through text-slate-400 mr-1">{item.price.toFixed(2)}</span>{customPrice.toFixed(2)} CHF <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">{L('custom', 'ind.')}</span></>) : `${item.price.toFixed(2)} CHF`}
                          </p>
                        </div>
                        {isAccessory(item) && (
                          <button type="button" onClick={() => setCustomPriceFor(item)}
                            className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 flex items-center justify-center"
                            title={L('Individual price', 'Individueller Preis')} data-testid={`custom-price-${item.id}`}>
                            <PencilSimple size={15} weight="bold" />
                          </button>
                        )}
                        <div className="flex items-center border border-slate-200 rounded-lg bg-white">
                          <button onClick={() => updateCartQty(item, -1)} className="px-2.5 py-1.5 text-slate-500 hover:bg-slate-50 rounded-l-lg" data-testid={`dec-${item.id}`}><Minus size={14} weight="bold" /></button>
                          <button
                            type="button"
                            onClick={() => setNumpadFor({ mode: 'addcart', item, current: qty })}
                            className={`px-3 py-1.5 min-w-[36px] text-center text-sm font-bold border-x border-slate-200 transition-colors hover:bg-emerald-50 active:scale-95 ${qty > 0 ? 'text-emerald-700' : 'text-slate-300'}`}
                            data-testid={`qty-${item.id}`}
                            title={L('Tap to type quantity', 'Antippen zum Eingeben')}>
                            {qty}
                          </button>
                          <button onClick={() => updateCartQty(item, 1)} className="px-2.5 py-1.5 text-slate-500 hover:bg-slate-50 rounded-r-lg" data-testid={`inc-${item.id}`}><Plus size={14} weight="bold" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {cartItemCount > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <Button onClick={submitBatchAdd} className="w-full bg-emerald-500 text-white hover:bg-emerald-600 font-bold py-3" data-testid="submit-batch-add-bottom">
                <Plus size={16} className="mr-2" /> {L(`Add ${cartItemCount} item(s) — ${cartTotal.toFixed(2)} CHF`, `${cartItemCount} Artikel hinzufügen — ${cartTotal.toFixed(2)} CHF`)}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== CHECKOUT PAYMENT MODAL ===== */}
      <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white border-slate-200 text-slate-900 max-w-sm rounded-2xl" data-testid="tab-pay-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{L('Payment', 'Zahlung')}</DialogTitle>
            <DialogDescription className="text-slate-500">{L('Checkout selected items', 'Ausgewählte Positionen bezahlen')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Selected items summary */}
            <div className="bg-slate-50 rounded-xl p-3 space-y-1 max-h-[150px] overflow-y-auto">
              {Object.entries(checkout).map(([id, sel]) => {
                const item = tab?.items.find(i => i.id === id);
                if (!item) return null;
                return (
                  <div key={id} className="flex justify-between text-sm">
                    <span className="text-slate-700">
                      {sel.isQuarter ? `1/4 ${item.name}` : `${sel.qty}× ${item.name}`}
                    </span>
                    <span className="font-bold text-slate-900">
                      {sel.isQuarter ? (item.total / 4).toFixed(2) : (sel.qty * item.price).toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="bg-emerald-50 p-4 rounded-xl text-center border border-emerald-200">
              <p className="text-slate-500 text-xs uppercase font-semibold mb-1">Total</p>
              <p className="text-3xl font-extrabold text-emerald-600">{checkoutTotal.toFixed(2)} CHF</p>
            </div>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500 uppercase font-semibold flex items-center gap-2">
                  <Money size={14} className="text-green-600" /> {L('Cash', 'Bar')} (CHF)
                </Label>
                <Input type="number" step="0.10" min="0" value={splitCash} onChange={(e) => handleSplitCashChange(e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-900 rounded-lg text-lg font-bold" data-testid="tab-split-cash" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500 uppercase font-semibold flex items-center gap-2">
                  <CreditCard size={14} className="text-blue-600" /> {L('Card', 'Karte')} (CHF)
                </Label>
                <Input type="number" step="0.10" min="0" value={splitCard} onChange={(e) => handleSplitCardChange(e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-900 rounded-lg text-lg font-bold" data-testid="tab-split-card" />
              </div>
            </div>
            <TipInput value={tipAmount} onChange={setTipAmount} base={parseFloat(splitCard) || checkoutTotal} />
            {(parseFloat(tipAmount) || 0) > 0 && (
              <p className="text-sm text-slate-600 text-center" data-testid="tab-card-total-with-tip">
                {L('Card total', 'Karte total')}: <b>{((parseFloat(splitCard) || 0) + (parseFloat(tipAmount) || 0)).toFixed(2)} CHF</b>
              </p>
            )}
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => { setSplitCash(checkoutTotal.toFixed(2)); setSplitCard('0.00'); }} className="p-2 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-green-400 hover:bg-green-50">100% {L('Cash', 'Bar')}</button>
              <button onClick={() => { setSplitCash('0.00'); setSplitCard(checkoutTotal.toFixed(2)); }} className="p-2 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-blue-400 hover:bg-blue-50">100% {L('Card', 'Karte')}</button>
              <button onClick={() => { const h = (checkoutTotal / 2).toFixed(2); setSplitCash(h); setSplitCard((checkoutTotal - parseFloat(h)).toFixed(2)); }} className="p-2 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-purple-400 hover:bg-purple-50">50/50</button>
            </div>
            <button onClick={confirmCssCheckout} disabled={payLoading}
              className="w-full p-2.5 border border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700 rounded-lg text-sm font-bold hover:bg-fuchsia-600 hover:text-white hover:border-fuchsia-600 transition-colors disabled:opacity-50"
              title={L('CSS Coins — not counted in daily till (~2 weeks payout)', 'CSS Coins — nicht im Tages-Total (Auszahlung ~2 Wochen)')}
              data-testid="tab-pay-css-coins">
              {L(`Pay ${checkoutTotal.toFixed(2)} CHF with CSS Coins`, `${checkoutTotal.toFixed(2)} CHF mit CSS Coins bezahlen`)}
            </button>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => { setShowPayModal(false); setTipAmount(''); }} className="flex-1 border-slate-200 text-slate-600">{L('Cancel', 'Abbrechen')}</Button>
              <Button onClick={confirmCheckout} disabled={payLoading} className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="confirm-tab-payment">
                <Check size={16} className="mr-1" /> {payLoading ? '...' : L('Confirm', 'Bestätigen')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Numpad popover for quick qty entry ====== */}
      <NumPad
        open={!!numpadFor}
        initial={numpadFor?.current || 0}
        max={numpadFor?.max ?? null}
        title={numpadFor ? `${numpadFor.item?.name || ''} × ?` : ''}
        subtitle={numpadFor?.max != null ? L(`Max ${numpadFor.max} unpaid`, `Max. ${numpadFor.max} offen`) : null}
        onConfirm={(n) => {
          if (!numpadFor) return;
          if (numpadFor.mode === 'addcart') {
            // Replace cart line for this item
            const it = numpadFor.item;
            setAddCart(prev => {
              if (n <= 0) { const { [it.id]: _, ...rest } = prev; return rest; }
              return { ...prev, [it.id]: { ...it, ...(prev[it.id] || {}), qty: n } };
            });
          } else if (numpadFor.mode === 'checkout') {
            setCheckoutItemQty(numpadFor.item, n);
          }
          setNumpadFor(null);
        }}
        onClose={() => setNumpadFor(null)}
      />

      {/* ====== Voucher selection dialog ====== */}
      <Dialog open={!!voucherDialog} onOpenChange={(o) => { if (!o) setVoucherDialog(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white border-slate-200 text-slate-900 max-w-md" data-testid="voucher-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-purple-700">
              🎟 {L('Apply voucher', 'Gutschein einlösen')}
            </DialogTitle>
            <DialogDescription>
              {L('Choose which customer voucher to deduct from and how much.',
                 'Wähle den Gutschein und den Betrag zum Einlösen.')}
            </DialogDescription>
          </DialogHeader>
          {voucherDialog && (
            <div className="space-y-4 my-2">
              {/* Item info */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs">
                <div className="font-semibold text-slate-900">{voucherDialog.item.name}</div>
                <div className="text-slate-500">
                  {L('Open balance:', 'Offener Betrag:')}{' '}
                  <strong>{((voucherDialog.item.total || 0) - (voucherDialog.item.paid_amount || 0)).toFixed(2)} CHF</strong>
                </div>
              </div>

              {/* Customer name (defaulted to tab customer) */}
              <div>
                <Label className="text-xs text-slate-600">{L('Customer', 'Kunde')}</Label>
                <UserSearchInline
                  API={API}
                  language={language}
                  initialValue={voucherDialog.customer_name}
                  onSelect={(u) => changeVoucherCustomer(u.id, u.name)}
                />
              </div>

              {/* Voucher dropdown */}
              <div>
                <Label className="text-xs text-slate-600">{L('Voucher', 'Gutschein')}</Label>
                {voucherDialog.loading ? (
                  <p className="text-xs text-slate-400 italic mt-1">{L('Loading vouchers…', 'Lade Gutscheine…')}</p>
                ) : voucherDialog.vouchers.length === 0 ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-1">
                    {L('This customer has no active vouchers.', 'Dieser Kunde hat keine aktiven Gutscheine.')}
                  </p>
                ) : (
                  <select
                    value={voucherDialog.voucher_id}
                    onChange={(e) => setVoucherDialog(prev => ({ ...prev, voucher_id: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    data-testid="voucher-select"
                  >
                    {voucherDialog.vouchers.map(v => (
                      <option key={v.id} value={v.id}>
                        {v.code || v.id.slice(-6)} · {(v.remaining_balance ?? v.amount ?? 0).toFixed(2)} CHF {L('remaining', 'verbleibend')}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Amount */}
              <div>
                <Label className="text-xs text-slate-600">{L('Amount (CHF)', 'Betrag (CHF)')}</Label>
                <Input
                  type="number"
                  step="0.05"
                  min="0"
                  value={voucherDialog.amount}
                  onChange={(e) => setVoucherDialog(prev => ({ ...prev, amount: e.target.value }))}
                  className="border-slate-200 font-mono text-right"
                  data-testid="voucher-amount"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setVoucherDialog(null)} className="border-slate-200" data-testid="voucher-cancel">
              {L('Cancel', 'Abbrechen')}
            </Button>
            <Button
              onClick={submitVoucherDialog}
              disabled={!voucherDialog?.voucher_id || payLoading || !(parseFloat(voucherDialog?.amount) > 0)}
              className="bg-purple-500 hover:bg-purple-600 text-white font-bold"
              data-testid="voucher-apply"
            >
              {payLoading
                ? L('Applying…', 'Wird angewendet…')
                : L('Apply voucher', 'Gutschein einlösen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PayConfirmDialog
        confirm={payConfirm}
        customerName={tab.customer_name}
        loading={payLoading}
        onCancel={() => setPayConfirm(null)}
        onConfirm={runPayConfirm}
        L={L}
      />
    </div>
  );
};

// ---------- lightweight user search (used by voucher dialog) ----------
// Uses the dedicated `/users/search?q=…` endpoint which is optimised for
// dropdowns (returns a flat array, max 20 results, admin/staff/trainer only).
// The previous version called `/users?search=…` which the backend silently
// ignored (list endpoint expects `q=`) — so the dropdown either showed the
// first 5 users alphabetically or nothing at all, and picking a name did
// nothing useful. Switching to the search endpoint plus a "selected" flag
// stops the effect from re-firing after a pick and keeps the chosen name
// stable in the input.
const UserSearchInline = ({ API, language, initialValue = '', onSelect }) => {
  const [q, setQ] = React.useState(initialValue);
  const [results, setResults] = React.useState([]);
  const [showList, setShowList] = React.useState(false);
  const [selected, setSelected] = React.useState(false);
  React.useEffect(() => {
    if (selected) return;
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await API.get(`/users/search?q=${encodeURIComponent(q)}&limit=8`);
        const list = Array.isArray(data) ? data : (Array.isArray(data?.users) ? data.users : []);
        setResults(list.slice(0, 8));
      } catch { setResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, API, selected]);
  return (
    <div className="relative">
      <input
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); setSelected(false); setShowList(true); }}
        onFocus={() => setShowList(true)}
        onBlur={() => setTimeout(() => setShowList(false), 200)}
        placeholder={language === 'de' ? 'Kunde suchen (Name/E-Mail)…' : 'Search customer (name/email)…'}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
        data-testid="voucher-user-search"
        autoComplete="off"
      />
      {showList && q && q.length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-auto">
          {results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400 italic">
              {language === 'de' ? 'Keine Treffer' : 'No matches'}
            </div>
          ) : (
            results.map(u => (
              <button
                key={u.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(u);
                  setQ(u.name);
                  setSelected(true);
                  setShowList(false);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50"
                data-testid={`voucher-user-result-${u.id}`}
              >
                <div className="font-semibold text-slate-900">{u.name}</div>
                <div className="text-[10px] text-slate-400">{u.email}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CourtTab;
